import clsx from 'clsx';
import { Switch } from '@hungryroot/pho';
import { type Tag, type PatchedCustomerTagList } from '@hungryroot/rest';
import {
  getCustomerTagsQueryKey,
  tagGroups,
  tags,
  type TagGroups,
} from '@hungryroot/tags';
import { usePatchCustomerTag } from '../../hooks/usePatchCustomerTag';
import { getTagDetails } from './getTagDetails';
import { DefaultTagLoader } from './tagLoaders/DefaultTagLoader';
import { LowSodiumHardConstrainTagLoader } from './tagLoaders/LowSodiumHardConstraintTagLoader';
import {
  type Warning,
  type TagLoaderComponent,
  type TagSelectionMap,
} from './types';
import {
  buildDislikesData,
  buildDislikesSelectedMap,
  buildLikesData,
  buildLikesSelectedMap,
  getIsEnabled,
  getSwitchIcon,
} from './utils';
import styles from './ControlTagSwitch.module.css';

export enum TagPreference {
  Selected = 1,
  NotSelected = 0,
}

export enum Variant {
  Like = 'like',
  Dislike = 'dislike',
}

interface ControlTagSwitchUIProps {
  tags: Tag[];
  getSwitchIcon?: (tag: Tag) => React.ReactNode;
  getIsEnabled: (tag: Tag) => boolean;
  testid?: string;
  handleChange: (tag: Tag, selected: boolean) => void;
  colCount?: 1 | 2;
}

/** Renders the UI for the Control Tag Switches */
export function ControlTagSwitchUI({
  tags,
  getIsEnabled,
  testid = 'control-tag-switch',
  handleChange,
  colCount = 2,
}: ControlTagSwitchUIProps) {
  const switchGroup = tags.map((tag) => {
    const details = getTagDetails(tag.id);
    const { name } = details;
    const isEnabled = getIsEnabled(tag);

    return (
      <Switch
        key={tag.id}
        testid={`${testid}-${tag.id}`}
        label={name || tag.name}
        subtext={tag.remark}
        enabled={isEnabled}
        onChange={() => {
          handleChange(tag, !isEnabled);
        }}
        icon={getSwitchIcon(tag)}
      />
    );
  });

  return (
    <div
      data-testid={testid}
      className={clsx({
        [styles['col-1']]: colCount === 1,
        [styles['col-2']]: colCount === 2,
      })}
    >
      {switchGroup}
    </div>
  );
}

type Props = {
  testid?: string;
  customerId: number;
  tagGroupId: TagGroups;
  variant?: Variant;
  colCount: 1 | 2;
  warnings?: Warning[];
};

const useControlTagSwitch = (props: Props) => {
  const buildSubmissionData =
    props.variant === Variant.Dislike ? buildDislikesData : buildLikesData;

  const buildSelectedMap =
    props.variant === Variant.Dislike
      ? buildDislikesSelectedMap
      : buildLikesSelectedMap;

  // The component responsible for loading the tags to be rendered, and filtering out any tags that should not be displayed
  const TagLoader: TagLoaderComponent =
    props.tagGroupId === tagGroups.nutrition
      ? LowSodiumHardConstrainTagLoader
      : DefaultTagLoader;

  return {
    /** Build the payload to submit to the api */
    buildSubmissionData,
    /** Build a map of tags mapped to if they are selected */
    buildSelectedMap,
    /** Fetch, sort, filter and expose an HOC to render tags */
    TagLoader,
  };
};

export function ControlTagSwitch(props: Props) {
  const { customerId, tagGroupId, warnings } = props;

  const { buildSubmissionData, buildSelectedMap, TagLoader } =
    useControlTagSwitch(props);

  const usePatchCustomerTagMutation = usePatchCustomerTag({
    invalidationKeys: [
      { queryKey: getCustomerTagsQueryKey([tags.OnlyLowSodium]) },
    ],
  });

  const handleChange =
    (selectedMap: TagSelectionMap, customerTags: PatchedCustomerTagList[]) =>
    (tag: Tag, selected: boolean) => {
      const { payload, count } = buildSubmissionData({
        tag,
        customerId,
        selected,
        customerTags,
      });

      warnings?.forEach((warning) => {
        const { show, fn } = warning({
          selected,
          tag,
          payload,
          count,
          selectedMap,
        });

        if (show)
          return fn({
            onConfirm: () => {
              usePatchCustomerTagMutation.mutate({ data: payload });
            },
          });
      });

      return usePatchCustomerTagMutation.mutate({ data: payload });
    };

  return (
    <TagLoader
      customerId={customerId}
      tagGroupId={tagGroupId}
      buildSelectedMap={buildSelectedMap}
    >
      {({ tags, selectedMap, customerTags }) => {
        return (
          <ControlTagSwitchUI
            {...props}
            getIsEnabled={getIsEnabled(selectedMap)}
            handleChange={handleChange(selectedMap, customerTags)}
            tags={tags}
          />
        );
      }}
    </TagLoader>
  );
}
